import { LinkspaceParam } from '@app/enums/linkspace-param.enum';
import { LinkspaceSection } from '@app/enums/linkspace-section.enum';
import { PageTypeEnum } from '@app/enums/page-type.enum';
import {
    BreadcrumbsModel,
    EsResponseManyModel,
    LinkspaceDocumentModel,
    LinkspaceModel,
    LinkspaceTagModel,
} from '@app/models/elastic-search';

export interface LinkspaceSectionConfig {
    docId: PageTypeEnum;
    sectionId: LinkspaceSection;
    limit?: number;
    prependLink?: LinkspaceTagModel;
}

const ACCOMMODATION_CITY_BREADCRUMB_INDEX = 2;
const ACCOMMODATION_CITY_CATEGORY_BREADCRUMB_INDEX = 3;

export const getLinksBySections = (
    hits: EsResponseManyModel<LinkspaceDocumentModel>['hits'],
    sectionConfig: LinkspaceSectionConfig[],
) => {
    const mappedSections: LinkspaceModel[] = [];

    sectionConfig.forEach(config => {
        const doc = hits.find(hit => hit._source.pageType === config.docId);
        const section = doc?._source.sections.find(section => section.id === config.sectionId);

        if (!section) {
            return;
        }

        const sectionLinks = Array.isArray(section.links) ? section.links : [];
        const links = config.prependLink ? [config.prependLink, ...sectionLinks] : sectionLinks;

        mappedSections.push({
            id: section.id,
            title: section.name,
            tags: config.limit ? links.slice(0, config.limit) : links,
        });
    });

    return mappedSections;
};

export const getAccommodationLinkspaces = (
    linkspaceDetails: EsResponseManyModel<LinkspaceDocumentModel> | null,
    breadcrumbs: BreadcrumbsModel,
) => {
    if (!linkspaceDetails?.hits) {
        return undefined;
    }

    const getBreadcrumbLink = (index: number) => {
        return breadcrumbs?.items?.[index]
            ? {
                  name: breadcrumbs.items[index].name,
                  url: breadcrumbs.items[index].item,
              }
            : undefined;
    };

    const sectionConfig = [
        {
            docId: PageTypeEnum.City,
            sectionId: LinkspaceSection.CityPopularNearby,
            limit: 30,
            prependLink: getBreadcrumbLink(ACCOMMODATION_CITY_BREADCRUMB_INDEX),
        },
        {
            docId: PageTypeEnum.CityCategory,
            sectionId: LinkspaceSection.CityCategoryPopularNearby,
            limit: 15,
            prependLink: getBreadcrumbLink(ACCOMMODATION_CITY_CATEGORY_BREADCRUMB_INDEX),
        },
        {
            docId: PageTypeEnum.City,
            sectionId: LinkspaceSection.CityRegion,
            limit: 15,
        },
        {
            docId: PageTypeEnum.City,
            limit: 9,
            sectionId: LinkspaceSection.CityCategory,
        },
        {
            docId: PageTypeEnum.City,
            sectionId: LinkspaceSection.CityPictogram,
            limit: 6,
        },
        {
            docId: PageTypeEnum.Accommodation,
            sectionId: LinkspaceSection.OtherAccommodationOptions,
        },
        {
            docId: PageTypeEnum.Accommodation,
            sectionId: LinkspaceSection.AccommodationCityCapacities,
            limit: 5,
        },
    ];

    return getLinksBySections(linkspaceDetails.hits, sectionConfig);
};

export interface PageTypeRequirement {
    paramName?: LinkspaceParam;
}

// A dictionary keyed by pageType, with optional paramName
// https://docs.google.com/spreadsheets/d/1lVkdWX120gLM6vhSMXy9O6KrhkyaM-glprp6qFBOpL4/
const pageTypeRequirements: Record<string, PageTypeRequirement> = {
    [PageTypeEnum.City]: {},
    [PageTypeEnum.CityCategory]: { paramName: LinkspaceParam.CategoryId },
    [PageTypeEnum.CityPictogram]: { paramName: LinkspaceParam.PictogramId },
    [PageTypeEnum.CitySpecialOffer]: {},
    [PageTypeEnum.CityService]: { paramName: LinkspaceParam.ServiceId },
    [PageTypeEnum.CityCenter]: {},
    [PageTypeEnum.CityCenterCategory]: { paramName: LinkspaceParam.CategoryId },
    [PageTypeEnum.CityCheapAccommodation]: {},
    [PageTypeEnum.CityCategoryCheapAccommodation]: { paramName: LinkspaceParam.CategoryId },
    [PageTypeEnum.CityOpinions]: {},

    [PageTypeEnum.Region]: {},
    [PageTypeEnum.RegionCategory]: { paramName: LinkspaceParam.CategoryId },
    [PageTypeEnum.RegionPictogram]: { paramName: LinkspaceParam.PictogramId },
    [PageTypeEnum.RegionCheapAccommodation]: {},
    [PageTypeEnum.RegionSpecialOfferPage]: {},
    [PageTypeEnum.RegionSpecialOffer]: {},

    [PageTypeEnum.Voivodeship]: {},
    [PageTypeEnum.VoivodeshipCategory]: { paramName: LinkspaceParam.CategoryId },
    [PageTypeEnum.VoivodeshipCenterCategory]: { paramName: LinkspaceParam.CategoryId },
    [PageTypeEnum.VoivodeshipSpecialOffer]: {},
    [PageTypeEnum.VoivodeshipSpecialOfferPage]: {},
    [PageTypeEnum.VoivodeshipService]: { paramName: LinkspaceParam.ServiceId },
    [PageTypeEnum.VoivodeshipOpinion]: {},
    [PageTypeEnum.VoivodeshipCenter]: {},
    [PageTypeEnum.VoivodeshipCheapAccommodation]: {},
    [PageTypeEnum.VoivodeshipCategoryPage]: { paramName: LinkspaceParam.CategoryId },
    [PageTypeEnum.VoivodeshipPictogram]: { paramName: LinkspaceParam.PictogramId },

    [PageTypeEnum.Country]: {},
    [PageTypeEnum.CountryCategory]: { paramName: LinkspaceParam.CategoryId },
    [PageTypeEnum.CountrySpecialOffer]: {},
    [PageTypeEnum.CountryCheapAccommodation]: {},
    [PageTypeEnum.CountryCategoryCheapAccommodation]: { paramName: LinkspaceParam.CategoryId },
    [PageTypeEnum.CountryPictogram]: { paramName: LinkspaceParam.PictogramId },
    [PageTypeEnum.CountryService]: { paramName: LinkspaceParam.ServiceId },
    [PageTypeEnum.CountrySpecialOfferPage]: {},
};

export const getPageTypeRequirement = (pageType: PageTypeEnum) => pageTypeRequirements[pageType];
